"use client";

import { safeErrorLogAttributes } from "@afar-research/client-runtime/errors";
import { DISPLAY_VIEW_WEBSOCKET_PATH, type ScopedThreadRef } from "@afar-research/contracts";
import { Camera } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { Button } from "~/components/ui/button";
import { useEnvironment } from "~/state/environments";
import { Tooltip, TooltipPopup, TooltipTrigger } from "~/components/ui/tooltip";

import { takeCloudSnapShot } from "./cloudSnapShot";
import { authorizeViewSocket, viewSocketRefusals } from "./viewSocket";

type ScreenState = "connecting" | "connected" | "disconnected";

/**
 * A Cloud Workspace's Cloud Display, live (ADR-0137): the screen the
 * Collaborative Browser and the computer-use server run on, which anyone
 * watching can also take over. The stream is open only while this panel is,
 * and while it is open the workspace's VM stays in use. A SnapShot
 * from here lands in the draft of the thread beside the panel.
 */
export function CloudScreenView(props: { readonly threadRef: ScopedThreadRef }) {
  const { threadRef } = props;
  const environmentId = threadRef.environmentId;
  const environmentLabel = useEnvironment(environmentId)?.label ?? null;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [state, setState] = useState<ScreenState>("connecting");
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    let disposed = false;
    let rfb: { disconnect: () => void } | undefined;
    const abort = new AbortController();
    const refusals = viewSocketRefusals(DISPLAY_VIEW_WEBSOCKET_PATH);
    setState("connecting");
    void (async () => {
      const url = await authorizeViewSocket(
        environmentId,
        DISPLAY_VIEW_WEBSOCKET_PATH,
        abort.signal,
        refusals,
      );
      if (disposed) return;
      if (url === null) {
        setState("disconnected");
        return;
      }
      // Loaded on demand: only a Cloud Workspace's screen panel needs a VNC client.
      const { default: RFB } = await import("@novnc/novnc");
      if (disposed) return;
      const client = new RFB(container, url, { shared: true });
      client.scaleViewport = true;
      client.resizeSession = false;
      let connected = false;
      client.addEventListener("connect", () => {
        connected = true;
        setState("connected");
      });
      client.addEventListener("disconnect", () => {
        if (disposed) return;
        if (!connected) refusals.warn("it closed before it opened.");
        setState("disconnected");
      });
      rfb = client;
    })().catch((error: unknown) => {
      if (disposed) return;
      refusals.warn("the screen viewer failed to start.", { ...safeErrorLogAttributes(error) });
      setState("disconnected");
    });
    return () => {
      disposed = true;
      abort.abort();
      rfb?.disconnect();
    };
  }, [attempt, environmentId]);

  const takeSnapShot = () => {
    // noVNC draws the display into a canvas of its own inside the container.
    const canvas = containerRef.current?.querySelector("canvas");
    if (!canvas) return;
    void takeCloudSnapShot(threadRef, canvas, {
      kind: "cloud-display",
      environmentLabel,
    });
  };

  return (
    <div className="relative flex min-h-0 flex-1 flex-col bg-background">
      <div className="flex items-center gap-2 border-b border-border px-3 py-1.5 text-xs text-muted-foreground">
        <span className="mr-auto">Cloud Display</span>
        <span>Keeps this workspace's agent compute running while open</span>
        <Tooltip>
          <TooltipTrigger
            render={
              <Button
                variant="ghost"
                size="icon-xs"
                onClick={takeSnapShot}
                aria-label="SnapShot to draft"
                type="button"
                disabled={state !== "connected"}
              />
            }
          >
            <Camera />
          </TooltipTrigger>
          <TooltipPopup>SnapShot to draft</TooltipPopup>
        </Tooltip>
      </div>
      <div
        ref={containerRef}
        data-cloud-display-input
        className="relative min-h-0 flex-1 overflow-hidden"
      />
      {state !== "connected" ? (
        <div className="absolute inset-0 top-8 grid place-items-center gap-2 text-center text-xs text-muted-foreground">
          {state === "connecting" ? (
            "Starting the Cloud Display…"
          ) : (
            <div className="grid gap-2">
              <span>The Cloud Display is not connected.</span>
              <Button size="sm" variant="outline" onClick={() => setAttempt((value) => value + 1)}>
                Reconnect
              </Button>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}
